import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { Hint } from 'ui/Hint';
import { Label } from 'ui/Label';
import { InputError } from 'ui/InputError';
import { fillArray } from 'utilities/FillArray';
import Button, { ButtonSize, ButtonVariant } from 'ui/Button';

export type NumberSelectProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  max: number;
  label?: string;
  error?: string;
  required?: boolean;
  hint?: ReactNode;
  className?: string;
  compact?: boolean;
};

// A radio button select for numbers. So you can select 1, 2, 3, 4 as buttons when you have set max to 4.
export default function NumberSelectInput<T extends FieldValues>({
  name,
  max,
  control,
  label,
  error,
  required,
  hint,
  className,
  compact = false,
}: NumberSelectProps<T>): JSX.Element {
  return (
    <div className={classNames('group relative', className)}>
      {label && (
        <Label>
          {label} {required && '*'}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <div className='flex gap-2 mt-2'>
              {fillArray(max).map(opt => (
                <Button
                  key={opt + 1}
                  type='button'
                  onClick={() => {
                    field.onChange(field.value === opt + 1 ? undefined : opt + 1);
                  }}
                  size={compact ? ButtonSize.Small : ButtonSize.Normal}
                  variant={field.value === opt + 1 ? ButtonVariant.Primary : ButtonVariant.Default}
                >
                  {opt + 1}
                </Button>
              ))}
            </div>
          );
        }}
      />
      {hint && <Hint>{hint}</Hint>}
      {error && <InputError>{error}</InputError>}
    </div>
  );
}
